import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Card,
  Grid,
} from '@mui/material';
import {
  Business,
  BarChart,
  Group,
  Logout,
  Notifications,
  Link as LinkIcon,
  Language,
  LinkedIn,
  WhatsApp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../styles/theme';
import { getUserInitials } from '../utils/userInfo';
import { authService } from '../services/authService';

const UsefulLinks = () => {
  const navigate = useNavigate();

  const sidebarItems = [
    { id: 'analytics', text: 'Dashboard', icon: <BarChart /> },
    { id: 'colleagues', text: 'Colleagues', icon: <Group /> },
    { id: 'useful-links', text: 'Useful Links', icon: <LinkIcon /> },
  ];

  const handleSidebarClick = (itemId) => {
    if (itemId === 'analytics') {
      navigate('/home');
    } else if (itemId === 'colleagues') {
      navigate('/home');
    } else if (itemId === 'org-chart') {
      navigate('/home');
    }
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: still clear local storage and navigate
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('user');
      localStorage.removeItem('userToken');
      navigate('/');
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const userInitials = getUserInitials();

  const usefulLinks = [
    {
      id: 1,
      title: 'WEBSITE',
      description: 'Official company website with services, insights, and company information',
      url: 'https://airolabs.ai/',
      icon: <Language sx={{ fontSize: 40, color: 'white' }} />,
      backgroundColor: '#4CAF50',
    },
    {
      id: 2,
      title: 'LINKEDIN',
      description: 'Follow us on LinkedIn for company updates, news, and professional insights',
      url: 'https://www.linkedin.com/company/airolabsai',
      icon: <LinkedIn sx={{ fontSize: 40, color: 'white' }} />,
      backgroundColor: '#FFC107',
    },
    {
      id: 3,
      title: 'ZIMYO',
      description: 'Access your HR portal for attendance, leave management, and employee services',
      url: 'https://zimyo.work/login',
      icon: <Business sx={{ fontSize: 40, color: 'white' }} />,
      backgroundColor: '#2196F3',
    },
    {
      id: 4,
      title: 'WHATSAPP',
      description: 'Join our team WhatsApp group for quick updates and informal communication',
      url: 'https://chat.whatsapp.com/EigbPwTTpCtATg9m9qTwQe',
      icon: <WhatsApp sx={{ fontSize: 40, color: 'white' }} />,
      backgroundColor: '#FF5722',
    },
  ];

  const handleLinkClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', height: '100vh', backgroundColor: 'white' }}>
        {/* Left Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              backgroundColor: '#0D004A',
              border: 'none',
            },
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#0D004A', fontWeight: 'bold', fontSize: '1.2rem' }}
                >
                  airo
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.5rem' }}
              >
                KYC
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, pt: 2 }}>
            <List>
              {sidebarItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ px: 2, mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleSidebarClick(item.id)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: item.id === 'useful-links' ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: item.id === 'useful-links' ? 'bold' : 'normal',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                  <Logout />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ color: 'white' }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <AppBar
            position="static"
            elevation={0}
            sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}
          >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D004A' }}>
                Useful Links
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton sx={{ color: '#666666' }}>
                  <Notifications />
                </IconButton>
                <Avatar
                  onClick={handleProfileClick}
                  sx={{
                    bgcolor: '#FF0000',
                    width: 40,
                    height: 40,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {userInitials}
                </Avatar>
              </Box>
            </Toolbar>
          </AppBar>

          <Box sx={{ flexGrow: 1, p: 4, backgroundColor: 'white', overflowY: 'auto' }}>
            <Grid container spacing={4} sx={{ maxWidth: 800, margin: '0 auto' }}>
              {usefulLinks.map((link) => (
                <Grid item xs={12} sm={6} key={link.id}>
                  <Card
                    onClick={() => handleLinkClick(link.url)}
                    sx={{
                      height: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 3,
                      backgroundColor: link.backgroundColor,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        {link.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: 'white',
                          fontSize: '1.1rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {link.title}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default UsefulLinks;